import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import "./PostFeed.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? "https://api.soaltriskelions.com"
        : "http://localhost:5000");

const reactionLabels = {
    like: "\uD83D\uDC4D",
    heart: "\u2764\uFE0F",
    fire: "\uD83D\uDD25",
};

const extraReactionLabels = {
    dislike: "\uD83D\uDC4E",
    smile: "\uD83D\uDE0A",
    haha: "\uD83D\uDE02",
};

const reactionOrder = [
    ["like", reactionLabels.like, "Like"],
    ["dislike", extraReactionLabels.dislike, "Dislike"],
    ["heart", reactionLabels.heart, "Heart"],
    ["smile", extraReactionLabels.smile, "Smile"],
    ["haha", extraReactionLabels.haha, "Haha"],
    ["fire", reactionLabels.fire, "Fire"],
];

const IMAGE_LIMIT = 2 * 1024 * 1024;
const ATTACHMENT_LIMIT = 5 * 1024 * 1024;
const VIDEO_LIMIT = 25 * 1024 * 1024;
const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];
const videoExtensions = [".mp4", ".mov", ".m4v", ".webm", ".avi"];
const pdfExtensions = [".pdf"];

function getToken() {
    return localStorage.getItem("token") || "";
}

function getExtension(value = "") {
    const clean = String(value).split("?")[0].toLowerCase();
    const dot = clean.lastIndexOf(".");
    return dot >= 0 ? clean.slice(dot) : "";
}

function isImageFile(file) {
    return file?.type?.startsWith("image/") || imageExtensions.includes(getExtension(file?.name));
}

function isVideoFile(file) {
    return file?.type?.startsWith("video/") || videoExtensions.includes(getExtension(file?.name));
}

function isPdfFile(file) {
    return file?.type === "application/pdf" || pdfExtensions.includes(getExtension(file?.name));
}

function isVideoUrl(url = "", type = "") {
    return String(type).startsWith("video/") || videoExtensions.includes(getExtension(url));
}

function isPdfAttachment(comment) {
    return comment?.attachment_type === "application/pdf" || pdfExtensions.includes(getExtension(comment?.attachment_url));
}

function validatePostMedia(file) {
    if (isImageFile(file)) {
        return file.size <= IMAGE_LIMIT ? "" : "Each image must be 2 MB or smaller.";
    }
    if (isVideoFile(file)) {
        return file.size <= VIDEO_LIMIT ? "" : "Each video must be 25 MB or smaller.";
    }
    return "Only JPG, JPEG, PNG, GIF, MP4, MOV, M4V, WebM, and AVI files are allowed.";
}

function validateCommentAttachment(file) {
    if (isImageFile(file)) {
        return file.size <= ATTACHMENT_LIMIT ? "" : "Comment image must be 5 MB or smaller.";
    }
    if (isPdfFile(file)) {
        return file.size <= ATTACHMENT_LIMIT ? "" : "PDF attachment must be 5 MB or smaller.";
    }
    if (isVideoFile(file)) {
        return file.size <= VIDEO_LIMIT ? "" : "Comment video must be 25 MB or smaller.";
    }
    return "Only JPG, JPEG, PNG, GIF, PDF, MP4, MOV, M4V, WebM, and AVI files are allowed.";
}

function formatDate(value) {
    if (!value) return "";
    return new Date(value).toLocaleString("en-US", {
        month: "long",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatPostAge(value) {
    if (!value) return "";

    const postedAt = new Date(value);
    const diffMs = Date.now() - postedAt.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return formatDate(value);

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const month = 30 * day;

    if (diffMs < minute) return "Just now";
    if (diffMs < hour) {
        const minutes = Math.floor(diffMs / minute);
        return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    if (diffMs < day) {
        const hours = Math.floor(diffMs / hour);
        return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    if (diffMs < 2 * day) return "Yesterday";
    if (diffMs < month) {
        const days = Math.floor(diffMs / day);
        return `${days} day${days === 1 ? "" : "s"} ago`;
    }

    const months = Math.floor(diffMs / month);
    return `${months} month${months === 1 ? "" : "s"} ago`;
}

function getCommentPhoto(comment) {
    if (!comment?.author_primaryid || !comment?.author_gender) return null;
    const folder = comment.author_gender === "Sigma" ? "Sigma-Photo" : "Phi-Photo";
    return `${API_BASE}/UploadPhoto/${folder}/${comment.author_primaryid}.jpg`;
}

function getPostAuthorPhoto(post) {
    if (!post?.author_primaryid) return null;
    const gender =
        post.author_gender ||
        (String(post.author_primaryid).toUpperCase().startsWith("SIG-") ? "Sigma" : "Phi");
    return `${API_BASE}/UploadPhoto/${gender}-Photo/${post.author_primaryid}.jpg`;
}

function initials(name) {
    return String(name || "Member")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}

function firstNameOnly(comment) {
    return comment.author_firstname || (comment.author_name || "").split(" ")[0] || "Member";
}

export default function PostFeed({ onOpenMemberProfile }) {
    const [posts, setPosts] = useState([]);
    const [canPin, setCanPin] = useState(false);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [photos, setPhotos] = useState([]);
    const [pinned, setPinned] = useState(false);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [postWarning, setPostWarning] = useState("");
    const [postUploadStatus, setPostUploadStatus] = useState("");
    const [commentDrafts, setCommentDrafts] = useState({});
    const [commentAttachments, setCommentAttachments] = useState({});
    const [commentWarnings, setCommentWarnings] = useState({});
    const [commentSending, setCommentSending] = useState({});
    const [commentsCollapsed, setCommentsCollapsed] = useState({});
    const [replyOpen, setReplyOpen] = useState({});
    const [replyDrafts, setReplyDrafts] = useState({});
    const [replySending, setReplySending] = useState({});
    const [openReactionPost, setOpenReactionPost] = useState(null);
    const reactionCloseTimer = useRef(null);
    const [photoViewer, setPhotoViewer] = useState(null);
    const [zoomedProfileAvatar, setZoomedProfileAvatar] = useState(null);
    const [reactionDetailsPost, setReactionDetailsPost] = useState(null);
    const totalPhotos = useMemo(() => photos.length, [photos]);

    useEffect(() => {
        if (!zoomedProfileAvatar) return undefined;

        const closeZoomedAvatar = (event) => {
            if (event.target.closest(".profile-avatar-zoom, .profile-avatar-fallback-zoom, .reaction-detail-avatar-button")) return;
            setZoomedProfileAvatar(null);
        };

        document.addEventListener("click", closeZoomedAvatar);
        return () => document.removeEventListener("click", closeZoomedAvatar);
    }, [zoomedProfileAvatar]);

    const loadPosts = useCallback(async () => {
        setMsg("");
        try {
            const res = await fetch(`${API_BASE}/api/posts?includeHidden=true`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to load posts.");
                return;
            }
            setCanPin(Boolean(data.can_pin));
            setPosts(data.posts || []);
        } catch {
            setMsg("Network error while loading posts.");
        }
    }, []);

    useEffect(() => {
        loadPosts();
    }, [loadPosts]);

    useEffect(() => {
        if (!msg) return undefined;
        const timer = setTimeout(() => setMsg(""), 3000);
        return () => clearTimeout(timer);
    }, [msg]);

    useEffect(() => {
        if (!photoViewer) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setPhotoViewer(null);
            if (event.key === "ArrowLeft") {
                setPhotoViewer((viewer) => ({
                    ...viewer,
                    index: (viewer.index - 1 + viewer.photos.length) % viewer.photos.length,
                }));
            }
            if (event.key === "ArrowRight") {
                setPhotoViewer((viewer) => ({
                    ...viewer,
                    index: (viewer.index + 1) % viewer.photos.length,
                }));
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [photoViewer]);

    const openPhotoViewer = (post, index) => {
        const imagePhotos = (post.photos || []).filter((photo) => !isVideoUrl(photo.photo_url));
        const selectedPhoto = post.photos?.[index];
        const imageIndex = Math.max(
            0,
            imagePhotos.findIndex((photo) => photo.id === selectedPhoto?.id)
        );
        setPhotoViewer({
            title: post.title,
            photos: imagePhotos,
            index: imageIndex,
            variant: "post",
        });
    };

    const openCommentAttachmentViewer = (comment) => {
        setPhotoViewer({
            title: comment.attachment_name || "Comment attachment",
            photos: [{ photo_url: comment.attachment_url }],
            index: 0,
            variant: "comment",
        });
    };

    const movePhotoViewer = (direction) => {
        setPhotoViewer((viewer) => ({
            ...viewer,
            index: (viewer.index + direction + viewer.photos.length) % viewer.photos.length,
        }));
    };

    const handlePhotoSelect = async (event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = "";
        if (!selected.length) return;
        setPostWarning("");
        setPostUploadStatus("Uploading.....");

        const remaining = Math.max(0, 5 - photos.length);
        const nextFiles = selected.slice(0, remaining);
        if (selected.length > remaining) {
            setPostWarning("Maximum 5 uploads per post.");
        }

        try {
            const media = [];
            for (const file of nextFiles) {
                const warning = validatePostMedia(file);
                if (warning) {
                    setPostWarning(warning);
                    continue;
                }
                if (isVideoFile(file)) {
                    media.push(file);
                    continue;
                }
                const output = await imageCompression(file, {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    fileType: file.type || "image/jpeg",
                });
                media.push(
                    new File([output], file.name, {
                        type: output.type || file.type || "image/jpeg",
                    })
                );
            }
            setPhotos((value) => [...value, ...media].slice(0, 5));
        } catch {
            setPostWarning("Upload preparation failed.");
        } finally {
            setPostUploadStatus("");
        }
    };

    const removePhoto = (index) => {
        setPhotos((value) => value.filter((_, i) => i !== index));
    };

    const replacePost = (post) => {
        setPosts((value) =>
            value
                .map((item) => (item.id === post.id ? post : item))
                .sort((a, b) => Number(b.pinned) - Number(a.pinned) || new Date(b.created_at) - new Date(a.created_at))
        );
    };

    const createPost = async (event) => {
        event.preventDefault();
        if (!title.trim() || !body.trim()) {
            setPostWarning("Title and details are required.");
            return;
        }

        setBusy(true);
        setMsg("");
        setPostWarning("");
        setPostUploadStatus("Uploading.....");
        const form = new FormData();
        form.append("title", title.trim());
        form.append("body", body.trim());
        form.append("pinned", canPin && pinned ? "true" : "false");
        photos.forEach((photo) => form.append("photos", photo));

        try {
            const res = await fetch(`${API_BASE}/api/posts`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: form,
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setPostWarning(data?.error || "Failed to create post.");
                return;
            }
            setPosts((value) => [data.post, ...value]);
            setTitle("");
            setBody("");
            setPhotos([]);
            setPinned(false);
            setMsg("Post created.");
        } catch {
            setPostWarning("Network error while creating post.");
        } finally {
            setBusy(false);
            setPostUploadStatus("");
        }
    };

    const toggleReaction = async (postId, reaction) => {
        try {
            setOpenReactionPost(null);
            const res = await fetch(`${API_BASE}/api/posts/${postId}/reactions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ reaction }),
            });
            const data = await res.json();
            if (data.ok) replacePost(data.post);
        } catch {
            setMsg("Reaction failed.");
        }
    };

    const getViewerReaction = (post) => post.reactions?.viewer?.[0] || "";

    const getReactionTotal = (post) =>
        reactionOrder.reduce((total, [reaction]) => total + (post.reactions?.[reaction] || 0), 0);

    const getReactionSummary = (post) =>
        reactionOrder
            .filter(([reaction]) => (post.reactions?.[reaction] || 0) > 0)
            .map(([reaction, label]) => `${label} ${post.reactions?.[reaction]}`)
            .join("  ");

    const formatReactionTime = (value) => {
        if (!value) return "";
        return new Date(value).toLocaleString([], {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const openReactionPicker = (postId) => {
        if (reactionCloseTimer.current) {
            clearTimeout(reactionCloseTimer.current);
        }
        setOpenReactionPost(postId);
    };

    const scheduleReactionPickerClose = () => {
        if (reactionCloseTimer.current) {
            clearTimeout(reactionCloseTimer.current);
        }
        reactionCloseTimer.current = setTimeout(() => setOpenReactionPost(null), 180);
    };

    const addComment = async (postId) => {
        const comment = (commentDrafts[postId] || "").trim();
        const attachment = commentAttachments[postId];
        if (!comment && !attachment) return;
        if (commentSending[postId]) return;

        try {
            setCommentWarnings((value) => ({ ...value, [postId]: "" }));
            if (attachment) {
                const warning = validateCommentAttachment(attachment);
                if (warning) {
                    setCommentWarnings((value) => ({ ...value, [postId]: warning }));
                    return;
                }
            }
            setCommentSending((value) => ({ ...value, [postId]: true }));
            const form = new FormData();
            form.append("comment", comment);
            if (attachment) form.append("attachment", attachment);
            const res = await fetch(`${API_BASE}/api/posts/${postId}/comments`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: form,
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setCommentWarnings((value) => ({ ...value, [postId]: data?.error || "Failed to add comment." }));
                return;
            }
            replacePost(data.post);
            setCommentDrafts((value) => ({ ...value, [postId]: "" }));
            setCommentAttachments((value) => ({ ...value, [postId]: null }));
        } catch {
            setCommentWarnings((value) => ({ ...value, [postId]: "Network error while adding comment." }));
        } finally {
            setCommentSending((value) => ({ ...value, [postId]: false }));
        }
    };

    const removeComment = async (postId, comment) => {
        if (!window.confirm("Remove this comment?")) return;

        try {
            const res = await fetch(`${API_BASE}/api/posts/${postId}/comments/${comment.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to remove comment.");
                return;
            }
            replacePost(data.post);
            setMsg("Comment removed.");
        } catch {
            setMsg("Network error while removing comment.");
        }
    };

    const getReplyKey = (postId, commentId) => `${postId}:${commentId}`;

    const toggleProfileAvatarZoom = (key) => {
        setZoomedProfileAvatar((current) => (current === key ? null : key));
    };

    const addReply = async (postId, commentId) => {
        const key = getReplyKey(postId, commentId);
        const reply = (replyDrafts[key] || "").trim();
        if (!reply || replySending[key]) return;

        try {
            setReplySending((value) => ({ ...value, [key]: true }));
            const res = await fetch(`${API_BASE}/api/posts/${postId}/comments/${commentId}/replies`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ reply }),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to add reply.");
                return;
            }
            replacePost(data.post);
            setReplyDrafts((value) => ({ ...value, [key]: "" }));
            setReplyOpen((value) => ({ ...value, [key]: false }));
        } catch {
            setMsg("Network error while adding reply.");
        } finally {
            setReplySending((value) => ({ ...value, [key]: false }));
        }
    };

    const removeReply = async (postId, commentId, reply) => {
        if (!window.confirm("Remove this reply?")) return;

        try {
            const res = await fetch(`${API_BASE}/api/posts/${postId}/comments/${commentId}/replies/${reply.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to remove reply.");
                return;
            }
            replacePost(data.post);
            setMsg("Reply removed.");
        } catch {
            setMsg("Network error while removing reply.");
        }
    };

    const togglePin = async (post) => {
        try {
            const res = await fetch(`${API_BASE}/api/posts/${post.id}/pin`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ pinned: !post.pinned }),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to update pin.");
                return;
            }
            replacePost(data.post);
        } catch {
            setMsg("Network error while updating pin.");
        }
    };

    const toggleHidden = async (post) => {
        try {
            const res = await fetch(`${API_BASE}/api/posts/${post.id}/hide`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ hidden: !post.hidden_by_viewer }),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to update post visibility.");
                return;
            }
            replacePost(data.post);
        } catch {
            setMsg("Network error while updating post visibility.");
        }
    };

    const removePost = async (post) => {
        if (!window.confirm("Remove this post?")) return;

        try {
            const res = await fetch(`${API_BASE}/api/posts/${post.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                setMsg(data?.error || "Failed to remove post.");
                return;
            }
            setPosts((value) => value.filter((item) => item.id !== post.id));
            setMsg("Post removed.");
        } catch {
            setMsg("Network error while removing post.");
        }
    };

    return (
        <div className="post-feed">
            <form className="post-composer" onSubmit={createPost}>
                <div className="post-composer-head">
                    <h2>Post Feed</h2>
                    <button type="submit" className="post-create-top" disabled={busy}>
                        {busy ? "Uploading....." : "Create Post"}
                    </button>
                </div>

                <input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Post title"
                    maxLength={20}
                />
                <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    placeholder="Share event details, announcements, reminders..."
                    rows={4}
                />

                {photos.length > 0 && (
                    <div className="composer-attachment-box">
                        <div className="composer-attachment-head">
                            <span>Media Attachment</span>
                            <span>{totalPhotos}/5</span>
                        </div>
                        <div className="post-photo-preview">
                            {photos.map((photo, index) => (
                                <div key={`${photo.name}-${index}`}>
                                    {isVideoFile(photo) ? (
                                        <video src={URL.createObjectURL(photo)} controls muted />
                                    ) : (
                                        <img src={URL.createObjectURL(photo)} alt="preview" />
                                    )}
                                    <button type="button" onClick={() => removePhoto(index)}>
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="post-composer-actions">
                    <label
                        className="post-photo-btn"
                        title="Max 5 uploads per post. Images max 2 MB. Videos max 25 MB."
                    >
                        Upload Photos/Videos
                        <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.m4v,.webm,.avi,image/*,video/*"
                            multiple
                            onChange={handlePhotoSelect}
                            hidden
                        />
                    </label>
                    <span>{totalPhotos}/5 uploads</span>
                    {canPin && (
                        <label className="pin-check">
                            <input
                                type="checkbox"
                                checked={pinned}
                                onChange={(event) => setPinned(event.target.checked)}
                            />
                            Pin important post
                        </label>
                    )}
                </div>

            </form>

                        {postUploadStatus && <div className="upload-status">{"\u23F3"} {postUploadStatus}</div>}
                        {postWarning && <div className="upload-warning">{"\u26A0\uFE0F"} {postWarning}</div>}

            {msg && <div className="post-message">{msg}</div>}

            <div className="post-list">
                {posts.length === 0 ? (
                    <div className="post-empty">No posts yet.</div>
                ) : (
                    posts.map((post) => (
                        post.hidden_by_viewer && !post.can_remove ? (
                            <article className="post-card post-card-hidden" key={post.id}>
                                <div>
                                    <h3>Hidden Post</h3>
                                    <div className="post-meta">
                                        This post from {post.author_name} is hidden only from your feed.
                                    </div>
                                </div>
                                <button type="button" onClick={() => toggleHidden(post)}>
                                    Unhide
                                </button>
                            </article>
                        ) : (
                        <article className={`post-card ${post.pinned ? "pinned" : ""}`} key={post.id}>
                            <div className="post-topline">
                                <div className="post-author-heading">
                                    <button
                                        type="button"
                                        className={`post-author-avatar fallback profile-avatar-fallback-zoom ${
                                            zoomedProfileAvatar === `post-${post.id}` ? "is-zoomed" : ""
                                        }`}
                                        onClick={() => toggleProfileAvatarZoom(`post-${post.id}`)}
                                        aria-label={`Zoom ${post.author_name || "post author"} avatar`}
                                    >
                                        {initials(post.author_name)}
                                    </button>
                                    {getPostAuthorPhoto(post) && (
                                        <button
                                            type="button"
                                            className={`profile-avatar-zoom ${
                                                zoomedProfileAvatar === `post-${post.id}` ? "is-zoomed" : ""
                                            }`}
                                            onClick={() => toggleProfileAvatarZoom(`post-${post.id}`)}
                                            aria-label={`Zoom ${post.author_name || "post author"} profile photo`}
                                        >
                                            <img
                                                className="post-author-avatar"
                                                src={getPostAuthorPhoto(post)}
                                                alt=""
                                                onError={(event) => {
                                                    event.currentTarget.parentElement.style.display = "none";
                                                }}
                                            />
                                        </button>
                                    )}
                                    <div className="post-author-text">
                                    <h3>{post.pinned ? "\uD83D\uDCCC " : ""}{post.title}</h3>
                                    <div className="post-meta" title={formatDate(post.created_at)}>
                                        Posted by{" "}
                                        <button
                                            type="button"
                                            className="feed-author-link"
                                            onClick={() => onOpenMemberProfile?.(post)}
                                        >
                                            {post.author_name}
                                        </button>{" "}
                                        · {formatPostAge(post.created_at)}
                                    </div>
                                </div>
                                </div>
                                <div className="post-tools">
                                    {canPin && (
                                        <button type="button" onClick={() => togglePin(post)}>
                                            {post.pinned ? "Unpin" : "Pin"}
                                        </button>
                                    )}
                                    {post.can_remove ? (
                                        <button type="button" className="post-remove-btn" onClick={() => removePost(post)}>
                                            Remove
                                        </button>
                                    ) : (
                                        <button type="button" onClick={() => toggleHidden(post)}>
                                            {post.hidden_by_viewer ? "Unhide" : "Hide"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <p className="post-body">{post.body}</p>

                            {post.photos?.length > 0 && (
                                <>
                                    <div className="post-photos">
                                        {post.photos.map((photo, index) =>
                                            isVideoUrl(photo.photo_url) ? (
                                                <video
                                                    key={photo.id}
                                                    className="post-video"
                                                    src={photo.photo_url}
                                                    controls
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="post-photo-open"
                                                    key={photo.id}
                                                    onClick={() => openPhotoViewer(post, index)}
                                                    aria-label={`Open photo ${index + 1} of ${post.photos.length}`}
                                                >
                                                    <img src={photo.photo_url} alt="post" />
                                                </button>
                                            )
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="reaction-row">
                                <div
                                    className="reaction-picker-wrap"
                                    onMouseEnter={() => openReactionPicker(post.id)}
                                    onMouseLeave={scheduleReactionPickerClose}
                                >
                                    <button
                                        type="button"
                                        className={`reaction-count reaction-like-trigger ${
                                            getViewerReaction(post) === "like" ? "active" : ""
                                        }`}
                                        title="Choose reaction"
                                        aria-label="Choose reaction"
                                        onClick={() => openReactionPicker(post.id)}
                                        onFocus={() => openReactionPicker(post.id)}
                                    >
                                        {reactionLabels.like} Like
                                    </button>
                                    <div
                                        className={`reaction-picker ${
                                            openReactionPost === post.id ? "is-open" : ""
                                        }`}
                                        role="menu"
                                    >
                                        {reactionOrder.map(([reaction, label, name]) => (
                                            <button
                                                type="button"
                                                key={reaction}
                                                title={name}
                                                aria-label={name}
                                                className={getViewerReaction(post) === reaction ? "active" : ""}
                                                onClick={() => toggleReaction(post.id, reaction)}
                                            >
                                                <span>{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {getReactionTotal(post) > 0 && (
                                    <button
                                        type="button"
                                        className="reaction-summary-btn"
                                        title="View reaction details"
                                        aria-label="View reaction details"
                                        onClick={() => setReactionDetailsPost(post)}
                                    >
                                        {getReactionSummary(post)}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`comment-toggle-btn ${
                                        commentsCollapsed[post.id] !== false ? "collapsed" : ""
                                    }`}
                                    title={commentsCollapsed[post.id] !== false ? "Show comments" : "Hide comments"}
                                    aria-expanded={commentsCollapsed[post.id] === false}
                                    onClick={() =>
                                        setCommentsCollapsed((value) => ({
                                            ...value,
                                            [post.id]: value[post.id] === false,
                                        }))
                                    }
                                >
                                    {"\uD83D\uDCAC"} {post.comments?.length || 0} Comments
                                </button>
                            </div>

                            <div className={commentsCollapsed[post.id] !== false ? "comments-panel collapsed" : "comments-panel"}>
                            <div className="comment-list">
                                {(post.comments || []).map((comment) => {
                                    const replyKey = getReplyKey(post.id, comment.id);
                                    return (
                                    <div className="comment-item" key={comment.id}>
                                        <span className="comment-avatar-wrap">
                                            <button
                                                type="button"
                                                className={`comment-avatar fallback profile-avatar-fallback-zoom ${
                                                    zoomedProfileAvatar === `comment-${comment.id}` ? "is-zoomed" : ""
                                                }`}
                                                onClick={() => toggleProfileAvatarZoom(`comment-${comment.id}`)}
                                                aria-label={`Zoom ${firstNameOnly(comment)} avatar`}
                                            >
                                                {firstNameOnly(comment).charAt(0).toUpperCase()}
                                            </button>
                                            {getCommentPhoto(comment) && (
                                                <button
                                                    type="button"
                                                    className={`profile-avatar-zoom ${
                                                        zoomedProfileAvatar === `comment-${comment.id}` ? "is-zoomed" : ""
                                                    }`}
                                                    onClick={() => toggleProfileAvatarZoom(`comment-${comment.id}`)}
                                                    aria-label={`Zoom ${firstNameOnly(comment)} profile photo`}
                                                >
                                                    <img
                                                        className="comment-avatar"
                                                        src={getCommentPhoto(comment)}
                                                        alt=""
                                                        onError={(event) => {
                                                            event.currentTarget.parentElement.style.display = "none";
                                                        }}
                                                    />
                                                </button>
                                            )}
                                        </span>
                                        <div className="comment-author-line">
                                            <button
                                                type="button"
                                                className="feed-author-link comment-author-button"
                                                onClick={() => onOpenMemberProfile?.(comment)}
                                            >
                                                {firstNameOnly(comment)}
                                            </button>
                                            <span title={formatDate(comment.created_at)}>
                                                · {formatPostAge(comment.created_at)}
                                            </span>
                                        </div>
                                        <span>{comment.comment}</span>
                                        {comment.attachment_url && (
                                            isPdfAttachment(comment) ? (
                                                <a
                                                    className="comment-attachment pdf"
                                                    href={comment.attachment_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    PDF: {comment.attachment_name || "Open attachment"}
                                                </a>
                                            ) : isVideoUrl(comment.attachment_url, comment.attachment_type) ? (
                                                <video
                                                    className="comment-attachment video"
                                                    src={comment.attachment_url}
                                                    controls
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="comment-attachment image"
                                                    onClick={() => openCommentAttachmentViewer(comment)}
                                                    aria-label={`Open ${comment.attachment_name || "comment image"}`}
                                                >
                                                    <img src={comment.attachment_url} alt={comment.attachment_name || "Comment attachment"} />
                                                </button>
                                            )
                                        )}
                                        {comment.can_remove && (
                                            <button
                                                type="button"
                                                className="comment-remove-btn"
                                                onClick={() => removeComment(post.id, comment)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="comment-reply-btn"
                                            title="Reply Message"
                                            onClick={() =>
                                                setReplyOpen((value) => ({
                                                    ...value,
                                                    [replyKey]: !value[replyKey],
                                                }))
                                            }
                                        >
                                            Reply ({comment.replies?.length || 0})
                                        </button>
                                        {comment.replies?.length > 0 && (
                                            <div className="reply-list">
                                                {comment.replies.map((reply) => (
                                                    <div className="reply-item" key={reply.id}>
                                                        <span className="comment-avatar-wrap reply-avatar-wrap">
                                                            <button
                                                                type="button"
                                                                className={`comment-avatar fallback profile-avatar-fallback-zoom ${
                                                                    zoomedProfileAvatar === `reply-${reply.id}` ? "is-zoomed" : ""
                                                                }`}
                                                                onClick={() => toggleProfileAvatarZoom(`reply-${reply.id}`)}
                                                                aria-label={`Zoom ${firstNameOnly(reply)} avatar`}
                                                            >
                                                                {firstNameOnly(reply).charAt(0).toUpperCase()}
                                                            </button>
                                                            {getCommentPhoto(reply) && (
                                                                <button
                                                                    type="button"
                                                                    className={`profile-avatar-zoom ${
                                                                        zoomedProfileAvatar === `reply-${reply.id}` ? "is-zoomed" : ""
                                                                    }`}
                                                                    onClick={() => toggleProfileAvatarZoom(`reply-${reply.id}`)}
                                                                    aria-label={`Zoom ${firstNameOnly(reply)} profile photo`}
                                                                >
                                                                    <img
                                                                        className="comment-avatar"
                                                                        src={getCommentPhoto(reply)}
                                                                        alt=""
                                                                        onError={(event) => {
                                                                            event.currentTarget.parentElement.style.display = "none";
                                                                        }}
                                                                    />
                                                                </button>
                                                            )}
                                                        </span>
                                                        <div className="comment-author-line reply-author-line">
                                                            <button
                                                                type="button"
                                                                className="feed-author-link comment-author-button"
                                                                onClick={() => onOpenMemberProfile?.(reply)}
                                                            >
                                                                {firstNameOnly(reply)}
                                                            </button>
                                                            <span title={formatDate(reply.created_at)}>
                                                                · {formatPostAge(reply.created_at)}
                                                            </span>
                                                        </div>
                                                        <span>{reply.reply}</span>
                                                        {reply.can_remove && (
                                                            <button
                                                                type="button"
                                                                className="comment-remove-btn reply-remove-btn"
                                                                onClick={() => removeReply(post.id, comment.id, reply)}
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {replyOpen[replyKey] && (
                                            <div className="reply-form">
                                                <input
                                                    value={replyDrafts[replyKey] || ""}
                                                    onChange={(event) =>
                                                        setReplyDrafts((value) => ({
                                                            ...value,
                                                            [replyKey]: event.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") addReply(post.id, comment.id);
                                                    }}
                                                    placeholder="Write a reply..."
                                                />
                                                <button
                                                    type="button"
                                                    className="reply-send-btn"
                                                    title="Send reply"
                                                    aria-label="Send reply"
                                                    onClick={() => addReply(post.id, comment.id)}
                                                    disabled={Boolean(replySending[replyKey])}
                                                >
                                                    {replySending[replyKey] ? "..." : "\u27A4"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            <div className="comment-form">
                                <input
                                    value={commentDrafts[post.id] || ""}
                                    onChange={(event) =>
                                        setCommentDrafts((value) => ({
                                            ...value,
                                            [post.id]: event.target.value,
                                        }))
                                    }
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") addComment(post.id);
                                    }}
                                    placeholder="Write a comment..."
                                />
                                <label
                                    className={`comment-upload-btn ${commentAttachments[post.id] ? "selected" : ""}`}
                                    title={commentAttachments[post.id]?.name || "Upload JPG, JPEG, PNG, GIF, PDF, or video"}
                                >
                                    {"\uD83D\uDCE5"}
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.gif,.pdf,.mp4,.mov,.m4v,.webm,.avi,image/jpeg,image/png,image/gif,application/pdf,video/*"
                                        hidden
                                        onChange={(event) => {
                                            const file = event.target.files?.[0] || null;
                                            const warning = file ? validateCommentAttachment(file) : "";
                                            setCommentWarnings((value) => ({ ...value, [post.id]: warning }));
                                            setCommentAttachments((value) => ({ ...value, [post.id]: file }));
                                            event.target.value = "";
                                        }}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className="comment-send-btn"
                                    title="Send comment"
                                    aria-label="Send comment"
                                    onClick={() => addComment(post.id)}
                                    disabled={Boolean(commentSending[post.id])}
                                >
                                    {"\u27A4"}
                                </button>
                                {commentSending[post.id] && (
                                    <div className="comment-upload-status">{"\u23F3"} Uploading.....</div>
                                )}
                                {commentAttachments[post.id] && (
                                    <div className="comment-upload-status">
                                        <span>Upload ready: {commentAttachments[post.id].name}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCommentAttachments((value) => ({
                                                    ...value,
                                                    [post.id]: null,
                                                }));
                                                setCommentWarnings((value) => ({
                                                    ...value,
                                                    [post.id]: "",
                                                }));
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                {commentWarnings[post.id] && (
                                    <div className="upload-warning comment-warning">
                                        {"\u26A0\uFE0F"} {commentWarnings[post.id]}
                                    </div>
                                )}
                            </div>
                            </div>
                        </article>
                        )
                    ))
                )}
            </div>

            {reactionDetailsPost && (
                <div className="reaction-detail-modal" role="dialog" aria-modal="true" aria-label="Reaction details">
                    <button
                        type="button"
                        className="reaction-detail-backdrop"
                        onClick={() => setReactionDetailsPost(null)}
                        aria-label="Close reaction details"
                    />
                    <div className="reaction-detail-content">
                        <div className="reaction-detail-head">
                            <strong>Post Reactions</strong>
                            <button type="button" onClick={() => setReactionDetailsPost(null)}>
                                X
                            </button>
                        </div>
                        <div className="reaction-detail-summary">
                            {getReactionSummary(reactionDetailsPost)}
                        </div>
                        <div className="reaction-detail-list">
                            {(reactionDetailsPost.reactions?.details || []).length === 0 ? (
                                <div className="reaction-detail-empty">No reactions yet.</div>
                            ) : (
                                reactionDetailsPost.reactions.details.map((item) => {
                                    const reactionMeta = reactionOrder.find(([reaction]) => reaction === item.reaction);
                                    const label = reactionMeta?.[1] || reactionLabels.like;
                                    const name = reactionMeta?.[2] || "Like";
                                    return (
                                        <div className="reaction-detail-item" key={`${item.idname}-${item.created_at}`}>
                                            <button
                                                type="button"
                                                className={`reaction-detail-avatar reaction-detail-avatar-button ${
                                                    zoomedProfileAvatar === `reaction-${item.idname}-${item.created_at}` ? "is-zoomed" : ""
                                                }`}
                                                onClick={() => toggleProfileAvatarZoom(`reaction-${item.idname}-${item.created_at}`)}
                                                aria-label={`Zoom ${item.author_name || item.idname || "member"} avatar`}
                                            >
                                                {(item.author_firstname || item.author_name || item.idname || "?")
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </button>
                                            <div>
                                                <strong>{item.author_name || item.idname}</strong>
                                                <span>{formatReactionTime(item.created_at)}</span>
                                            </div>
                                            <span className="reaction-detail-emoji" title={name}>
                                                {label}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {photoViewer && (
                <div
                    className={`post-photo-modal ${photoViewer.variant === "comment" ? "comment-image-viewer" : ""}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label={photoViewer.variant === "comment" ? "Comment image viewer" : "Post photo viewer"}
                >
                    <button
                        type="button"
                        className="post-photo-modal-backdrop"
                        onClick={() => setPhotoViewer(null)}
                        aria-label="Close photo viewer"
                    />
                    <div className="post-photo-modal-content">
                        {photoViewer.variant === "comment" ? (
                            <button
                                type="button"
                                className="comment-image-viewer-close"
                                onClick={() => setPhotoViewer(null)}
                                aria-label="Close image viewer"
                            >
                                X
                            </button>
                        ) : (
                            <div className="post-photo-modal-head">
                                <strong>{photoViewer.title}</strong>
                                <button type="button" onClick={() => setPhotoViewer(null)}>
                                    X
                                </button>
                            </div>
                        )}
                        <div className="post-photo-modal-stage">
                            {photoViewer.variant !== "comment" && (
                                <button
                                    type="button"
                                    className="post-photo-nav previous"
                                    onClick={() => movePhotoViewer(-1)}
                                    disabled={photoViewer.photos.length < 2}
                                >
                                    &lt; Prev
                                </button>
                            )}
                            <img
                                src={photoViewer.photos[photoViewer.index].photo_url}
                                alt={`${photoViewer.title} ${photoViewer.index + 1}`}
                            />
                            {photoViewer.variant !== "comment" && (
                                <button
                                    type="button"
                                    className="post-photo-nav next"
                                    onClick={() => movePhotoViewer(1)}
                                    disabled={photoViewer.photos.length < 2}
                                >
                                    Next &gt;
                                </button>
                            )}
                        </div>
                        {photoViewer.variant !== "comment" && (
                            <div className="post-photo-modal-count">
                                Photo {photoViewer.index + 1} of {photoViewer.photos.length}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}




